import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { cn, formatCurrency } from '@/lib/utils';
import {
    useIncomeTimeseries,
    type IncomeBusinessLineFilter,
    type IncomeGranularity,
    type IncomeTimeseriesPoint,
} from '@/hooks/useAdminAnalytics';
import { ScrollChipRow } from '@/components/analytics/ScrollChipRow';

const LINE_COLOR = 'hsl(199, 89%, 48%)';

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

const LINE_OPTIONS: { value: IncomeBusinessLineFilter; label: string }[] = [
    { value: 'total', label: 'Total' },
    { value: 'subscription', label: 'Suscripciones' },
    { value: 'license', label: 'Licencias' },
    { value: 'store', label: 'Tienda' },
];

const GRANULARITY_OPTIONS: { value: IncomeGranularity; label: string }[] = [
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
];

const NOTE =
    'Ingreso = base imponible de lo que es de AIPECES (misma lógica que Operaciones → Resultado). Series hasta ayer (día cerrado, hora de Madrid).';

interface MonetizationTrendChartProps {
    days: number | null;
    isMobile?: boolean;
}

function centsToEuros(cents: number): number {
    return cents / 100;
}

function valueForLine(row: IncomeTimeseriesPoint, line: IncomeBusinessLineFilter): number {
    if (line === 'subscription') return row.subscriptionIncomeCents;
    if (line === 'license') return row.licenseIncomeCents;
    if (line === 'store') return row.storeIncomeCents;
    return row.totalIncomeCents;
}

function formatPeriodLabel(iso: string, granularity: IncomeGranularity, withYear = false): string {
    const [year, month, day] = iso.slice(0, 10).split('-');
    if (!year || !month || !day) return iso;

    if (granularity === 'month') {
        const d = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
        return d.toLocaleDateString('es-ES', {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC',
        });
    }

    if (granularity === 'week') {
        return withYear
            ? `Sem ${Number(day)}/${Number(month)}/${year}`
            : `Sem ${Number(day)}/${Number(month)}`;
    }

    return withYear ? `${Number(day)}/${Number(month)}/${year}` : `${Number(day)}/${Number(month)}`;
}

function formatEuroAxis(value: number): string {
    if (!Number.isFinite(value)) return '0';
    if (Math.abs(value) >= 1000) {
        return `${(value / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 })}k`;
    }
    return value.toLocaleString('es-ES', { maximumFractionDigits: 0 });
}

export function MonetizationTrendChart({ days, isMobile = false }: MonetizationTrendChartProps) {
    const [line, setLine] = useState<IncomeBusinessLineFilter>('total');
    const [granularity, setGranularity] = useState<IncomeGranularity>('month');
    const { data, isLoading, error } = useIncomeTimeseries(days, granularity);

    const activeLineLabel = LINE_OPTIONS.find((o) => o.value === line)?.label ?? 'Total';

    const chartData = useMemo(() => {
        return (data ?? []).map((row) => ({
            periodStart: row.periodStart,
            euros: centsToEuros(valueForLine(row, line)),
        }));
    }, [data, line]);

    const periodTotalEuros = useMemo(() => {
        return centsToEuros((data ?? []).reduce((acc, row) => acc + valueForLine(row, line), 0));
    }, [data, line]);

    return (
        <div className="space-y-3 min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">Evolución de monetización</h3>
                <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {isLoading ? '—' : `${formatCurrency(periodTotalEuros)} en el periodo`}
                    {' · '}
                    {activeLineLabel}
                </p>
            </div>

            <ScrollChipRow>
                {LINE_OPTIONS.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => setLine(opt.value)}
                        className={cn(
                            'shrink-0 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                            line === opt.value
                                ? 'text-white shadow-sm'
                                : 'bg-muted/50 text-muted-foreground hover:text-foreground',
                        )}
                        style={line === opt.value ? { backgroundColor: LINE_COLOR } : undefined}
                    >
                        {opt.label}
                    </button>
                ))}
            </ScrollChipRow>

            <div className="flex flex-wrap gap-1 w-full min-w-0">
                {GRANULARITY_OPTIONS.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => setGranularity(opt.value)}
                        className={cn(
                            'shrink-0 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                            granularity === opt.value
                                ? 'bg-muted text-foreground'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            <p className="text-[11px] text-muted-foreground">{NOTE}</p>

            <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0">
                <div className={isMobile ? 'h-[220px]' : 'h-[260px]'}>
                    {isLoading ? (
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                            <RefreshCw size={20} className="animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="h-full flex items-center justify-center text-sm text-rose-500 px-4 text-center">
                            {(error as Error).message || 'No se pudo cargar la evolución de ingresos.'}
                        </div>
                    ) : chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{
                                    top: 8,
                                    right: isMobile ? 4 : 8,
                                    bottom: isMobile ? 8 : 4,
                                    left: isMobile ? 0 : -4,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                <XAxis
                                    dataKey="periodStart"
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                    minTickGap={isMobile ? 36 : 24}
                                    interval="preserveStartEnd"
                                    tickFormatter={(val: string) =>
                                        formatPeriodLabel(String(val ?? ''), granularity)
                                    }
                                />
                                <YAxis
                                    width={isMobile ? 36 : 48}
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={formatEuroAxis}
                                />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    labelStyle={{ color: 'hsl(210 40% 98%)' }}
                                    labelFormatter={(val) =>
                                        formatPeriodLabel(String(val ?? ''), granularity, true)
                                    }
                                    formatter={(value: number) => [
                                        formatCurrency(Number(value) || 0),
                                        activeLineLabel,
                                    ]}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="euros"
                                    stroke={LINE_COLOR}
                                    strokeWidth={2}
                                    dot={false}
                                    name={activeLineLabel}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                            No hay datos para el rango seleccionado
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
